import type { DisplayRef } from '@repo/sdk/models/components';

import {
  ALARMS_BY_COMPRESSOR_TYPE,
  COMPRESSOR_ALARM_POINTS,
} from './compressor-alarm-code.constants';
import type { CompressorAlarm, CompressorAlarmRefs } from './compressor-alarm-code.types';

/**
 * A device's `AlarmCode` control point, `CompressorType` metric and `DecodeAlarmCode`
 * condition among its display refs, or nothing unless it has all three.
 */
export function findCompressorAlarmRefs(
  displayRefs: DisplayRef[] | undefined
): CompressorAlarmRefs | undefined {
  const find = ({ key, namespace }: { key: string; namespace: string }) =>
    displayRefs?.find(({ ref }) => ref.key === key && ref.namespace === namespace)?.ref;

  const alarmCode = find(COMPRESSOR_ALARM_POINTS.alarmCode);
  const compressorType = find(COMPRESSOR_ALARM_POINTS.compressorType);
  const decodeAlarmCode = find(COMPRESSOR_ALARM_POINTS.decodeAlarmCode);
  if (!alarmCode || !compressorType || !decodeAlarmCode) return undefined;

  return { alarmCode, compressorType, decodeAlarmCode };
}

/**
 * The readable alarm for a compressor's `AlarmCode`, from the table for its
 * `CompressorType`. Nothing for no alarm (`0`), a code the manual leaves blank, a code
 * the table doesn't list, or a compressor type without a table, so a facility whose
 * tables aren't set up yet shows no Alarm Code control.
 */
export function decodeCompressorAlarmCode(
  code: number | undefined,
  compressorType: string | undefined
): CompressorAlarm | undefined {
  if (!code || !compressorType) return undefined;

  const table = ALARMS_BY_COMPRESSOR_TYPE[compressorType.toLowerCase()];
  if (!table) return undefined;

  const entry = table[code];
  if (!entry) return undefined;
  return { code, ...entry };
}
